import CustomText from 'components/common/text/CustomText';
import T from 'components/common/text/T';
import { ProjectContext } from 'contexts/project/ProjectContext';
import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import cx from 'classnames';
import FileListFolder from './FileListFolder';
import { IConvertFile } from 'lib/types/clinic/fileTypes';
import { useDidUpdateEffect } from 'lib/utils';
import { useProjectFileStore } from 'store/local/projectFile';
import DropzoneWrapper from 'components/common/dropzone/DropzoneWrapper';
import { useProjectFiles } from 'lib/hooks/project/useProjectFiles';
import { AppActions } from 'store/actionCreators';
import ClinicModal from 'components/common/modal/ClinicModal';
import MuiWrapper from 'components/common/input/MuiWrapper';
import { FormControlLabel, Radio, RadioGroup } from '@material-ui/core';
import { useProject } from 'lib/hooks/project/useProject';
import CustomSpan from 'components/common/text/CustomSpan';
import {
  icon_etc,
  icon_lower,
  icon_occlusion,
  icon_photo,
  icon_upper,
} from 'components/base/images';

interface ProjectFileListProps {}

function ProjectFileList({}: ProjectFileListProps) {
  const { t } = useTranslation();
  const [enabledRemove, setEnabledRemove] = useState<boolean>(false);
  const [enabledDownload, setEnabledDownload] = useState<boolean>(false);
  const { ipcRenderer } = window;

  const { classifyClinicFiles } = useContext(ProjectContext);

  const {
    setIsCheckboxChecked,
    setIsOwnerStorage,
    isOwnerStorage,
    setIsShareStorage,
    isShareStorage,
    isLocalStorage,
    setIsLocalStorage,
    isOpenRemoveModal,
    isOpenDuplicateModal,
    isOpenDuplicateModalType,
    uploadLocalFiles,
    setUploadLocalFiles,
  } = useProjectFileStore();
  const { hasAlarm } = useProject();
  const {
    handleClearAllCheckbox,
    handleClearAllView,
    handleAddLocalFiles,
    updateLocalFileList,
    duplicateLocalFile,
    localFileUploadIpc,
    handleClickFileDownload,
    handleOpenFileRemove,
    handleCloseFileRemove,
    handleConfirmFileRemove,
    handleCloseDuplicateModal,
    handleClickUploadToPartner,
    handleClickUploadToGroup,
    handleConfirmDuplicateModal,
  } = useProjectFiles();

  const {
    checkedClinicFiles,
    upperFiles,
    lowerFiles,
    occlusionFiles,
    photoFiles,
    etcFiles,
    noneFiles,
    clinicLocalFileList,
  } = classifyClinicFiles;

  useEffect(() => {
    if (!ipcRenderer) return;
    ipcRenderer.on(
      'importFileRes',
      (e: any, data: { status: boolean; directoryType: string; files: any[] }) => {
        if (data?.files?.length === 0) return;
        const fileObj = data.files.reduce((acc: any[], curr: any) => {
          const type = curr.fileName.slice(curr.fileName.lastIndexOf('.') + 1);
          const obj = {
            name: curr.fileName,
            path: curr.filePath,
            type,
          };
          return acc.concat(obj);
        }, []);
        setUploadLocalFiles(fileObj);
      },
    );
  }, []);

  useEffect(() => {
    if (checkedClinicFiles.value.length < 1 || (!isOwnerStorage && !isLocalStorage)) {
      setEnabledRemove(false);
    } else {
      setEnabledRemove(true);
    }
  }, [checkedClinicFiles.value, isOwnerStorage, isLocalStorage]);

  useEffect(() => {
    if (checkedClinicFiles.value.length < 1 || (!isOwnerStorage && !isShareStorage)) {
      setEnabledDownload(false);
    } else {
      setEnabledDownload(true);
    }
  }, [checkedClinicFiles.value, isOwnerStorage, isShareStorage]);

  useDidUpdateEffect(() => {
    if (checkedClinicFiles.value.length < 1) {
      setIsCheckboxChecked(false);
      setIsOwnerStorage(false);
      setIsShareStorage(false);
      setIsLocalStorage(false);
    } else {
      checkedClinicFiles.value.forEach((file: IConvertFile) => {
        setIsCheckboxChecked(true);
        setIsOwnerStorage(!!file.isOwnerStorage);
        setIsShareStorage(!!file.isShareStorage);
        setIsLocalStorage(!!file.isLocalStorage);
      });
    }
  }, [checkedClinicFiles.value]);

  useDidUpdateEffect(() => {
    if (uploadLocalFiles.length < 1) return;
    // console.log('HDS] newLocalFileList 111________ ', uploadLocalFiles);
    const newLocalFileList = updateLocalFileList(uploadLocalFiles);
    // console.log('HDS] newLocalFileList 222________ ', uploadLocalFiles);

    const exsitingFile = clinicLocalFileList.value.find((prevItem: IConvertFile) => {
      const hasSameFile = !!newLocalFileList.find((newLocalFile: IConvertFile) => {
        return prevItem.name === newLocalFile.name;
      });
      return hasSameFile;
    });
    // console.log('HDS] newLocalFileList 333________ ', exsitingFile);

    if (exsitingFile) {
      AppActions.add_popup({
        isOpen: true,
        content: <T>PROJECT_UPLOAD_FILE_OVERWRITE</T>,
        onClick() {
          duplicateLocalFile(newLocalFileList);
        },
      });
    } else {
      // clinicMergeFileList.setValue((draft: IConvertFile[]) => draft.concat(newLocalFileList));
      if (ipcRenderer) {
        localFileUploadIpc(newLocalFileList);
      }
    }
  }, [uploadLocalFiles]);

  useEffect(() => {
    return () => {
      handleClearAllCheckbox();
      handleClearAllView();
    };
  }, []);

  return (
    <StyledProjectFileList data-component-name="ProjectFileList">
      <ClinicModal
        title={t('CLINIC_MODAL_TITLE_NOTICE')}
        isOpen={isOpenRemoveModal}
        onClose={handleCloseFileRemove}
        onCancel={handleCloseFileRemove}
        onConfirm={handleConfirmFileRemove}
      >
        <RemoveModal />
      </ClinicModal>
      <ClinicModal
        title={t('CLINIC_MODAL_TITLE_NOTICE')}
        isOpen={isOpenDuplicateModal}
        onClose={handleCloseDuplicateModal}
        onCancel={handleCloseDuplicateModal}
        onConfirm={() => {
          handleConfirmDuplicateModal(isOpenDuplicateModalType);
        }}
      >
        <DuplicateModal />
      </ClinicModal>

      <div className="projectFileList__header_box">
        <CustomText fontSize={16} fontColor="#222" fontWeight={400}>
          <T>PROJECT_FILE_LIST</T>
        </CustomText>
        <div className="projectFileList__button_box">
          <div
            className={cx('projectFileList__button download', {
              enabled: enabledDownload,
              disabled: !enabledDownload,
            })}
            onClick={() => {
              if (!enabledDownload) return;
              handleClickFileDownload();
            }}
          >
            <span>
              <T>GLOBAL_DOWNLOAD</T>
            </span>
          </div>
          <div
            className={cx('projectFileList__button delete', {
              enabled: enabledRemove,
              disabled: !enabledRemove,
            })}
            onClick={() => {
              if (!enabledRemove) return;
              handleOpenFileRemove();
            }}
          >
            <span>
              <T>GLOBAL_DELETE</T>
            </span>
          </div>
        </div>
      </div>
      <div className="projectFileList__contents_box">
        <DropzoneWrapper
          apiRequest={e => {
            if (!!hasAlarm) return;
            handleAddLocalFiles(e);
          }}
        >
          <div className="projectFileList__contents_list">
            {upperFiles.value.length > 0 && (
              <FileListFolder
                titleIcon={icon_upper}
                title={t('FILE_GROUP_UPPER')}
                myFiles={upperFiles.value}
              />
            )}
            {lowerFiles.value.length > 0 && (
              <FileListFolder
                titleIcon={icon_lower}
                title={t('FILE_GROUP_LOWER')}
                myFiles={lowerFiles.value}
              />
            )}
            {occlusionFiles.value.length > 0 && (
              <FileListFolder
                titleIcon={icon_occlusion}
                title={t('FILE_GROUP_BITE')}
                myFiles={occlusionFiles.value}
              />
            )}
            {noneFiles.value.length > 0 && (
              <FileListFolder
                titleIcon={icon_etc}
                title={t('FILE_GROUP_ETC_MODEL')}
                myFiles={noneFiles.value}
              />
            )}
            {photoFiles.value.length > 0 && (
              <FileListFolder
                titleIcon={icon_photo}
                title={t('FILE_GROUP_IMAGE')}
                myFiles={photoFiles.value}
              />
            )}

            {etcFiles.value.length > 0 && (
              <FileListFolder
                titleIcon={icon_etc}
                title={t('FILE_GROUP_ETC')}
                myFiles={etcFiles.value}
              />
            )}
          </div>
        </DropzoneWrapper>
      </div>
    </StyledProjectFileList>
  );
}

export default React.memo(ProjectFileList);

const StyledProjectFileList = styled.div`
  width: 100%;
  height: calc(100% - 72px);
  position: relative;

  .projectFileList__header_box {
    padding: 15px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    .projectFileList__button_box {
      display: flex;
      column-gap: 20px;
      .projectFileList__button {
        display: flex;
        align-items: center;
        column-gap: 10px;
        height: 24px;
        padding: 0;
        border-radius: 3px;
        font-size: 16px;
        &.enabled {
          color: #888;
          font-weight: 400;
          &:hover {
            color: #222;
            cursor: pointer;
          }
        }
        &.disabled {
          color: #e1e1e7;
        }
      }
    }
  }

  .projectFileList__contents_box {
    height: calc(100% - 54px);
    .projectFileList__contents_list {
      border-top: 1px solid #e1e1e7;
      border-bottom: 1px solid #e1e1e7;
      padding: 8px 0 2px 8px;
      height: 100%;
      overflow-y: scroll;
      display: flex;
      flex-direction: column;
      row-gap: 10px;
    }
  }
`;

function RemoveModal() {
  const { isOwnerStorage, isShareStorage, isLocalStorage, removeType, setRemoveType } =
    useProjectFileStore();

  useEffect(() => {
    setRemoveType('all');
  }, []);

  return (
    <StyledRemoveModal data-component-name="RemoveModal">
      <div className="removeModal__description">
        <CustomSpan>
          <T>PROJECT_REMOVE_QUESTION</T>
        </CustomSpan>
      </div>
      <MuiWrapper className="removeModal__option_box">
        <RadioGroup
          className="removeModal__option_list"
          name="removeType"
          value={removeType}
          onChange={(e: any) => {
            setRemoveType(e.target.value);
          }}
        >
          <FormControlLabel
            value={'partner'}
            disabled={!isShareStorage}
            control={<Radio color="primary" size="medium" />}
            className="removeModal__option partner"
            label={
              <div className="removeModal__option_label">
                <CustomSpan fontSize={16} fontWeight={400}>
                  <T>GLOBAL_PARTNER</T>
                </CustomSpan>
              </div>
            }
          />
          <FormControlLabel
            value={'group'}
            disabled={!isOwnerStorage}
            control={<Radio color="primary" size="medium" />}
            className="removeModal__option group"
            label={
              <div className="removeModal__option_label">
                <CustomSpan fontSize={16} fontWeight={400}>
                  <T>GLOBAL_GROUP</T>
                </CustomSpan>
                <CustomSpan fontSize={12} fontWeight={400} fontColor="red">
                  <T>REMOVE_GROUP_ALERT</T>
                </CustomSpan>
              </div>
            }
          />

          <FormControlLabel
            value={'local'}
            disabled={!isLocalStorage}
            control={<Radio color="primary" size="medium" />}
            className="removeModal__option local"
            label={
              <div className="removeModal__option_label">
                <CustomSpan fontSize={16} fontWeight={400}>
                  <T>GLOBAL_LOCAL</T>
                </CustomSpan>
              </div>
            }
          />
          <FormControlLabel
            value={'all'}
            control={<Radio color="primary" size="medium" />}
            className="removeModal__option all"
            label={
              <div className="removeModal__option_label">
                <CustomSpan fontSize={16} fontWeight={400}>
                  <T>GLOBAL_ALL</T>
                </CustomSpan>
              </div>
            }
          />
        </RadioGroup>
      </MuiWrapper>
    </StyledRemoveModal>
  );
}

const StyledRemoveModal = styled.div`
  .removeModal__description {
    padding: 20px 0;
  }
  .removeModal__option_box {
    width: 100% !important;
    display: inline-block !important;
    height: initial;
    .removeModal__option_list {
      display: flex !important;
      flex-direction: column !important;
      .removeModal__option {
        width: 100%;
        .removeModal__option_label {
          display: flex;
          align-items: flex-end;
          column-gap: 10px;
        }
      }
    }
  }
`;

function DuplicateModal() {
  return (
    <StyledDuplicateModal data-component-name="DuplicateModal">
      <div className="duplicateModal__description">
        <CustomSpan fontSize={16} fontWeight={400}>
          <T>MODAL_FILE_DUPLICATE</T>
        </CustomSpan>
      </div>
    </StyledDuplicateModal>
  );
}

const StyledDuplicateModal = styled.div`
  .duplicateModal__description {
    padding: 20px 0;
    line-height: 24px;
  }
`;
